<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { createConsumer } from '@rails/actioncable';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useMapGetter } from 'dashboard/composables/store';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ReportHeader from './components/ReportHeader.vue';
import ReportFilterSelector from './components/FilterSelector.vue';
import ReportMetricCard from './components/ReportMetricCard.vue';
import PdvSalesAgentTable from './components/PdvSalesAgentTable.vue';
import PdvSalesProductRanking from './components/PdvSalesProductRanking.vue';
import PdvSalesPaymentChart from './components/PdvSalesPaymentChart.vue';
import PdvSalesTimelineChart from './components/PdvSalesTimelineChart.vue';
import PdvSalesAgentFilter from './components/PdvSalesAgentFilter.vue';
import PdvSalesProductAgentModal from './components/PdvSalesProductAgentModal.vue';

const store = useStore();
const router = useRouter();
const { t } = useI18n();
const { accountScopedRoute, accountId } = useAccount();
const { isAdmin: isAdministrator } = useAdmin();
const currentUser = computed(() => store.getters.getCurrentUser);

const summary = useMapGetter('pdvSalesReports/getSummary');
const agents = useMapGetter('pdvSalesReports/getAgents');
const products = useMapGetter('pdvSalesReports/getProducts');
const payments = useMapGetter('pdvSalesReports/getPayments');
const uiFlags = useMapGetter('pdvSalesReports/getUIFlags');

const since = ref(0);
const until = ref(0);
const groupBy = ref(null);
const selectedAgentId = ref(null);
const selectedProduct = ref(null);

const formatCurrency = val =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
    val || 0
  );

const metricCards = computed(() => [
  {
    key: 'total_revenue',
    label: t('PDV_SALES_REPORT.TOTAL_REVENUE'),
    value: formatCurrency(summary.value.total_revenue),
    infoText: t('PDV_SALES_REPORT.TOTAL_REVENUE'),
  },
  {
    key: 'total_sales',
    label: t('PDV_SALES_REPORT.TOTAL_SALES'),
    value: String(summary.value.total_sales ?? 0),
    infoText: t('PDV_SALES_REPORT.TOTAL_SALES'),
  },
  {
    key: 'avg_ticket',
    label: t('PDV_SALES_REPORT.AVG_TICKET'),
    value: formatCurrency(summary.value.avg_ticket),
    infoText: t('PDV_SALES_REPORT.AVG_TICKET'),
  },
  {
    key: 'total_discount',
    label: t('PDV_SALES_REPORT.TOTAL_DISCOUNT'),
    value: formatCurrency(summary.value.total_discount),
    infoText: t('PDV_SALES_REPORT.TOTAL_DISCOUNT'),
  },
  {
    key: 'avg_delivery_fee',
    label: t('PDV_SALES_REPORT.AVG_DELIVERY_FEE'),
    value: formatCurrency(summary.value.avg_delivery_fee),
    infoText: t('PDV_SALES_REPORT.AVG_DELIVERY_FEE'),
  },
]);

const cableURL = () => {
  const { websocketURL = '' } = window.chatwootConfig || {};
  return websocketURL ? `${websocketURL}/cable` : '/cable';
};

let cable = null;

onMounted(() => {
  cable = createConsumer(cableURL());
  cable.subscriptions.create(
    {
      channel: 'PdvSalesChannel',
      account_id: accountId.value,
      pubsub_token: currentUser.value?.pubsub_token,
    },
    {
      received({ event }) {
        if (event === 'sale_consumed') fetchAll();
      },
    }
  );
});

onUnmounted(() => {
  cable?.disconnect();
  cable = null;
});

const fetchAll = () => {
  if (!since.value || !until.value || !groupBy.value?.period) return;
  const params = {
    since: since.value,
    until: until.value,
    groupBy: groupBy.value.period,
    agentId: selectedAgentId.value,
  };
  store.dispatch('pdvSalesReports/fetchSummary', params);
  store.dispatch('pdvSalesReports/fetchProducts', params);
  store.dispatch('pdvSalesReports/fetchPayments', params);
  if (isAdministrator.value) {
    store.dispatch('pdvSalesReports/fetchAgents', params);
  }
};

const onFilterChange = ({ from, to, groupBy: selectedGroupBy }) => {
  since.value = from;
  until.value = to;
  groupBy.value = selectedGroupBy;
  fetchAll();
};

const onAgentChange = agentId => {
  selectedAgentId.value = agentId;
  fetchAll();
};

const onSelectAgent = agent => {
  router.push(
    accountScopedRoute('pdv_sales_agent_show', { agentId: agent.agent_id })
  );
};

const onSelectProduct = product => {
  selectedProduct.value = product;
};

const onCloseProductModal = () => {
  selectedProduct.value = null;
};

const goToSalesList = () => {
  router.push(accountScopedRoute('pdv_sales_list'));
};
</script>

<template>
  <ReportHeader :header-title="t('PDV_SALES_REPORT.TITLE')">
    <NextButton
      icon="i-lucide-list"
      color="slate"
      ghost
      size="sm"
      :label="t('PDV_SALES_REPORT.VIEW_SALES_LIST')"
      @click="goToSalesList"
    />
  </ReportHeader>
  <div class="flex flex-col gap-4 pb-6">
    <div class="flex flex-col gap-2 md:flex-row md:items-end">
      <div class="flex-1">
        <ReportFilterSelector
          :show-agents-filter="false"
          show-group-by-filter
          :show-business-hours-switch="false"
          @filter-change="onFilterChange"
        />
      </div>
      <div v-if="isAdministrator" class="shrink-0 md:w-64">
        <PdvSalesAgentFilter @agent-change="onAgentChange" />
      </div>
    </div>

    <div class="flex flex-col gap-6 rounded-xl bg-n-slate-2 p-4">
      <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <ReportMetricCard
          v-for="card in metricCards"
          :key="card.key"
          :label="card.label"
          :value="card.value"
          :info-text="card.infoText"
        />
      </div>

      <div
        class="bg-white dark:bg-slate-900 rounded-xl border border-slate-100 dark:border-slate-800 p-4"
      >
        <h3
          class="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-3"
        >
          {{ $t('PDV_SALES_REPORT.TIMELINE_TITLE') }}
        </h3>
        <PdvSalesTimelineChart
          :timeseries="summary.timeseries || []"
          :is-loading="uiFlags.isFetchingSummary"
        />
      </div>

      <div
        v-if="isAdministrator"
        class="bg-white dark:bg-slate-900 rounded-xl border border-slate-100 dark:border-slate-800 p-4"
      >
        <h3
          class="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-3"
        >
          {{ t('PDV_SALES_REPORT.AGENT_TABLE.TITLE') }}
        </h3>
        <PdvSalesAgentTable
          :agents="agents"
          :is-loading="uiFlags.isFetchingAgents"
          @select-agent="onSelectAgent"
        />
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div
          class="bg-white dark:bg-slate-900 rounded-xl border border-slate-100 dark:border-slate-800 p-4"
        >
          <PdvSalesProductRanking
            :products="products"
            :is-loading="uiFlags.isFetchingProducts"
            :is-admin="isAdministrator"
            @select-product="onSelectProduct"
          />
        </div>
        <div
          class="bg-white dark:bg-slate-900 rounded-xl border border-slate-100 dark:border-slate-800 p-4"
        >
          <PdvSalesPaymentChart
            :payments="payments"
            :is-loading="uiFlags.isFetchingPayments"
          />
        </div>
      </div>
    </div>
  </div>
  <PdvSalesProductAgentModal
    :product="selectedProduct"
    :since="since"
    :until="until"
    @close="onCloseProductModal"
  />
</template>
